type AnalyticsData = Record<string, string | number | boolean>
type AnalyticsEvent = { name: string; data?: AnalyticsData }

const MAX_QUEUE_SIZE = 20
const MAX_RETRIES = 16
const RETRY_DELAY_MS = 250
const pendingEvents: AnalyticsEvent[] = []
let retryTimer: ReturnType<typeof setTimeout> | undefined
let retryCount = 0
const DEFAULT_ACTIONS: Record<string, string> = {
  film_click: 'open',
  film_view: 'view',
  social_click: 'open',
  newsletter_click: 'open',
  newsletter_submit: 'submit',
  donation_click: 'open',
  screening_click: 'open',
  contact_click: 'open',
  contact_submit: 'submit',
}

// Replit Publishing injects window.umami when project analytics is enabled.
declare global {
  interface Window {
    umami?: {
      track(name: string, data?: AnalyticsData): void
    }
  }
}

function safeData(data?: AnalyticsData): AnalyticsData | undefined {
  if (!data) return undefined
  return Object.keys(data).reduce<AnalyticsData>((result, key) => {
    const value = data[key]
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = value
    }
    return result
  }, {})
}

function flushPendingEvents(): void {
  if (typeof window === 'undefined') return

  if (!window.umami) {
    if (pendingEvents.length === 0) return
    if (retryCount >= MAX_RETRIES) {
      pendingEvents.length = 0
      retryTimer = undefined
      return
    }
    retryCount += 1
    retryTimer = setTimeout(flushPendingEvents, RETRY_DELAY_MS)
    return
  }

  const events = pendingEvents.splice(0, pendingEvents.length)
  retryCount = 0
  retryTimer = undefined
  events.forEach(({ name, data }) => {
    try {
      window.umami?.track(name, data)
    } catch {
      // Analytics must never interfere with the film experience.
    }
  })
}

export function trackEvent(name: string, data?: AnalyticsData): void {
  if (typeof window === 'undefined') return
  const eventData = safeData(data) ?? {}
  if (eventData.action === undefined) eventData.action = DEFAULT_ACTIONS[name] ?? 'engage'
  const event = { name, data: eventData }

  try {
    if (window.umami) {
      flushPendingEvents()
      window.umami.track(event.name, event.data)
      return
    }
  } catch {
    // Analytics must never interfere with the film experience.
  }

  if (pendingEvents.length >= MAX_QUEUE_SIZE) pendingEvents.shift()
  pendingEvents.push(event)
  if (!retryTimer) retryTimer = setTimeout(flushPendingEvents, RETRY_DELAY_MS)
}